import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { emitErrorToast } from "../../lib/errorToast";
import {
  readOfficePreview,
  type OfficePreviewFormat,
} from "../../lib/officePreview";
import { loadOfficeViewer, type OfficeViewer } from "../../lib/officeViewer";
import { spreadsheetCellPreview } from "../../lib/spreadsheetCellPreview";
import { ToggleButton, ToggleGroup } from "../HeadingToggle";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { Text } from "../Text";
import { Textarea } from "../Textarea";
import { Tooltip } from "../Tooltip";
import { WavyLine } from "../WavyLine";
import { downloadFile } from "./download";
import { DownloadButton } from "./DownloadButton";
import "./officeChrome.css";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 320px;
  gap: 8px;
`;

const Preview = styled("div")`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 8px;
  background: white;
  color: #222;
  color-scheme: light;
  outline: 0.5px solid var(--border-alpha);

  &[data-format="xlsx"] {
    color-scheme: inherit;
    --ooxml-xlsx-chrome-background: var(--surface-secondary);
    --ooxml-xlsx-chrome-surface: var(--surface-primary);
    --ooxml-xlsx-chrome-surface-muted: var(--surface-hover);
    --ooxml-xlsx-chrome-text: var(--text-primary);
    --ooxml-xlsx-chrome-text-muted: var(--text-secondary);
    --ooxml-xlsx-chrome-border: var(--border-alpha);
    --ooxml-xlsx-chrome-selection-background: var(--gray-200);
    --ooxml-xlsx-chrome-accent: var(--control-bg);
    --ooxml-xlsx-chrome-scrollbar-color: var(--alpha16) transparent;
    --ooxml-xlsx-focus-ring: var(--control-bg);

    .xlsx-tab-strip button {
      font-size: 12px !important;
      font-weight: 500 !important;
    }
  }
`;

const Host = styled("div")`
  position: absolute;
  inset: 0;
`;

const Status = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  background: var(--surface-secondary);
  color: var(--text-secondary);
  text-align: center;
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const FormulaBar = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  > div {
    min-width: 0;
  }
`;

export function Office({
  url,
  fileName,
  format,
}: {
  url: string;
  fileName?: string;
  format: OfficePreviewFormat;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<OfficeViewer | null>(null);
  const [status, setStatus] = useState("Loading preview…");
  const [downloading, setDownloading] = useState(false);
  const [updatingView, setUpdatingView] = useState(false);
  const [slide, setSlide] = useState<{ index: number; total: number } | null>(
    null
  );
  const [selectedCell, setSelectedCell] =
    useState<ReturnType<typeof spreadsheetCellPreview>>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const controller = new AbortController();
    const { signal } = controller;
    const mount = document.createElement("div");
    mount.style.height = "100%";
    host.append(mount);
    let viewer: OfficeViewer | undefined;
    const fail = () => {
      if (signal.aborted) return;
      viewer?.destroy();
      viewer = undefined;
      viewerRef.current = null;
      setSelectedCell(null);
      setSlide(null);
      setStatus("Couldn’t preview this file. Download it to open in Office.");
    };
    void (async () => {
      try {
        const bytes = await readOfficePreview(
          await fetch(url, { credentials: "include", signal })
        );
        if (signal.aborted) return;
        if (bytes === null) {
          setStatus(
            "This file is too large to preview. Download it to open in Office."
          );
          return;
        }
        const createViewer = await loadOfficeViewer(format);
        if (signal.aborted) return;
        viewer = createViewer(
          mount,
          fail,
          (context) => {
            if (!signal.aborted)
              setSelectedCell(spreadsheetCellPreview(context));
          },
          (index, total) => {
            if (!signal.aborted) setSlide({ index, total });
          }
        );
        viewerRef.current = viewer;
        await viewer.load(bytes);
        if (!signal.aborted && viewerRef.current === viewer) setStatus("");
      } catch {
        fail();
      }
    })();
    return () => {
      controller.abort();
      viewer?.destroy();
      viewerRef.current = null;
      mount.remove();
    };
  }, [url, format]);

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    await downloadFile(url, fileName ?? `output.${format}`).catch(() => {
      emitErrorToast({
        icon: "circle-off",
        message: "Couldn’t download this file. Please try again.",
        buttonLabel: "Dismiss",
      });
    });
    setDownloading(false);
  };

  const updateView = async (
    action:
      | "zoomIn"
      | "zoomOut"
      | "fitWidth"
      | "fitPage"
      | "nextSlide"
      | "prevSlide"
  ) => {
    const currentViewer = viewerRef.current;
    if (!currentViewer || updatingView) return;
    setUpdatingView(true);
    try {
      await currentViewer[action]?.();
    } catch {
      if (viewerRef.current === currentViewer) {
        emitErrorToast({
          icon: "circle-off",
          message: "Couldn’t update the preview. Please try again.",
          buttonLabel: "Dismiss",
        });
      }
    }
    if (viewerRef.current === currentViewer) setUpdatingView(false);
  };

  return (
    <Container>
      {format === "xlsx" && (
        <FormulaBar>
          <Input
            aria-label="Selected cell"
            readOnly
            value={selectedCell?.address ?? ""}
            style={{ width: 80, minWidth: 80, textAlign: "center" }}
          />
          <Text variant="text-small" color="var(--text-secondary)">
            fx
          </Text>
          <Textarea
            aria-label="Cell formula or value"
            readOnly
            rows={1}
            disabled={!!status}
            value={selectedCell?.value ?? ""}
            placeholder={
              !selectedCell
                ? "Select a cell to view its formula or value"
                : selectedCell.value === null
                  ? "Select a single cell to view its full formula or value"
                  : undefined
            }
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              maxHeight: 80,
              overflowY: "auto",
              fontFamily: "var(--font-mono)",
            }}
          />
        </FormulaBar>
      )}
      <Preview
        data-format={format}
        aria-label={`${format.toUpperCase()} preview`}
        aria-busy={status === "Loading preview…"}
      >
        <Host ref={hostRef} />
        {status && (
          <Status role="status">
            {status === "Loading preview…" && <WavyLine size={20} />}
            <Text variant="text-small">{status}</Text>
          </Status>
        )}
      </Preview>
      <Footer>
        <ToggleGroup>
          <Tooltip content="Zoom out">
            <ToggleButton
              aria-label="Zoom out"
              disabled={!!status || updatingView}
              onPress={() => updateView("zoomOut")}
            >
              <Icon name="minus" size={12} />
            </ToggleButton>
          </Tooltip>
          <Tooltip content="Zoom in">
            <ToggleButton
              aria-label="Zoom in"
              disabled={!!status || updatingView}
              onPress={() => updateView("zoomIn")}
            >
              <Icon name="plus" size={12} />
            </ToggleButton>
          </Tooltip>
          <Tooltip content={format === "pptx" ? "Fit slide" : "Fit width"}>
            <ToggleButton
              aria-label={format === "pptx" ? "Fit slide" : "Fit width"}
              disabled={!!status || updatingView}
              onPress={() =>
                updateView(format === "pptx" ? "fitPage" : "fitWidth")
              }
            >
              <Icon name="maximize-2" size={12} />
            </ToggleButton>
          </Tooltip>
        </ToggleGroup>
        {format === "pptx" && (
          <ToggleGroup>
            <Tooltip content="Previous slide">
              <ToggleButton
                aria-label="Previous slide"
                disabled={
                  !!status || updatingView || !slide || slide.index === 0
                }
                onPress={() => updateView("prevSlide")}
              >
                <Icon name="chevron-left" size={12} />
              </ToggleButton>
            </Tooltip>
            <span role="status" aria-label="Slide position">
              <Text variant="text-small">
                {slide
                  ? `Slide ${slide.index + 1} of ${slide.total}`
                  : "Slides"}
              </Text>
            </span>
            <Tooltip content="Next slide">
              <ToggleButton
                aria-label="Next slide"
                disabled={
                  !!status ||
                  updatingView ||
                  !slide ||
                  slide.index >= slide.total - 1
                }
                onPress={() => updateView("nextSlide")}
              >
                <Icon name="chevron-right" size={12} />
              </ToggleButton>
            </Tooltip>
          </ToggleGroup>
        )}
        <DownloadButton onPress={download} disabled={downloading}>
          <Icon name="arrow-down" size={12} />
          <Text variant="text-small-md">
            {downloading ? "Downloading…" : "Download"}
          </Text>
        </DownloadButton>
      </Footer>
    </Container>
  );
}
